import React, { useEffect, useState } from "react";
import { dateOnly } from "@/lib/dates";
import { api } from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { Users, Copy, Check, Mail, Crown, Send, MoreVertical, Trash2, RefreshCw, LogOut, Shield, X, UserCircle2 } from "lucide-react";
import Avatar from "@/components/Avatar";
import { toast } from "sonner";
import { roleLabel } from "@/lib/labels";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function MembersView({ workspaceId, currentUser, onUsersChanged }) {
  const { current, refreshWorkspaces, switchWorkspace, workspaces } = useWorkspace();
  const { subscribe } = useWorkspace();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState("");
  const [inviteRole, setInviteRole] = useState("member");

  const myRole = members.find((m) => m.id === currentUser.id)?.role || "member";
  const isAdmin = myRole === "admin" || myRole === "owner";
  const isOwner = myRole === "owner";

  const load = async () => {
    if (!workspaceId) return;
    const m = await api.get(`/workspaces/${workspaceId}/members`);
    setMembers(m.data);
    if (m.data.find((x) => x.id === currentUser.id && (x.role === "admin" || x.role === "owner"))) {
      try {
        const i = await api.get(`/workspaces/${workspaceId}/invites`);
        setInvites(i.data);
      } catch (_e) { setInvites([]); }
    } else {
      setInvites([]);
    }
  };

  useEffect(() => { load(); setNewName(current?.name || ""); }, [workspaceId, current?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh on relevant WS events
  useEffect(() => {
    return subscribe((ev) => {
      if (!["member_joined", "member_removed", "member_role_changed", "workspace_updated"].includes(ev.type)) return;
      if (ev.data?.workspace_id === workspaceId) {
        load();
        onUsersChanged?.();
      }
    });
  }, [subscribe, workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const invite = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setInviting(true);
    try {
      const r = await api.post(`/workspaces/${workspaceId}/invites`, { email: email.trim(), role: inviteRole });
      setEmail("");
      if (r.data.status === "added") toast.success("Un cont existent a fost adăugat în workspace");
      else toast.success(`Invitație creată pentru ${email}`);
      await load();
      onUsersChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Nu a reușit"); }
    finally { setInviting(false); }
  };

  const cancelInvite = async (inviteId) => {
    try {
      await api.delete(`/workspaces/${workspaceId}/invites/${inviteId}`);
      await load();
      toast.success("Invitația a fost anulată");
    } catch (e) { toast.error(e?.response?.data?.detail || "Nu a reușit"); }
  };

  const copyCode = async () => {
    if (!current?.invite_code) return;
    await navigator.clipboard.writeText(current.invite_code);
    setCopied(true); toast.success("Codul de invitație a fost copiat");
    setTimeout(() => setCopied(false), 1500);
  };

  const regenCode = async () => {
    try {
      await api.patch(`/workspaces/${workspaceId}`, { regen_code: true });
      await refreshWorkspaces();
      toast.success("Cod de invitație nou generat");
    } catch (e) { toast.error(e?.response?.data?.detail || "Nu a reușit"); }
  };

  const rename = async () => {
    if (!newName.trim() || newName.trim() === current?.name) { setRenaming(false); return; }
    try {
      await api.patch(`/workspaces/${workspaceId}`, { name: newName.trim() });
      await refreshWorkspaces();
      toast.success("Workspace redenumit");
      setRenaming(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Nu a reușit"); }
  };

  const setClient = async (uid, is_client) => {
    try {
      await api.patch(`/workspaces/${workspaceId}/members/${uid}/client`, { is_client });
      await load();
      toast.success(is_client ? "Marcat drept client" : "Nu mai e marcat drept client");
    } catch (e) { toast.error(e?.response?.data?.detail || "Nu a reușit"); }
  };

  const setRole = async (uid, role) => {
    try {
      await api.patch(`/workspaces/${workspaceId}/members/${uid}`, { role });
      await load();
      toast.success(`Rol schimbat în ${roleLabel(role)}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Nu a reușit"); }
  };

  const staff = members.filter((m) => m.role !== "guest");
  const guests = members.filter((m) => m.role === "guest");

  const removeMember = async (uid) => {
    try {
      await api.delete(`/workspaces/${workspaceId}/members/${uid}`);
      await load();
      onUsersChanged?.();
      toast.success("Membru scos");
    } catch (e) { toast.error(e?.response?.data?.detail || "Nu a reușit"); }
  };

  const leaveWorkspace = async () => {
    try {
      await api.post(`/workspaces/${workspaceId}/leave`);
      const remaining = workspaces.filter((w) => w.id !== workspaceId);
      await refreshWorkspaces();
      if (remaining.length > 0) switchWorkspace(remaining[0].id);
      else { localStorage.removeItem("vs_workspace"); window.location.reload(); }
      toast.success("Ai ieșit din workspace");
    } catch (e) { toast.error(e?.response?.data?.detail || "Nu a reușit"); }
  };

  const deleteWorkspace = async () => {
    try {
      await api.delete(`/workspaces/${workspaceId}`);
      const remaining = workspaces.filter((w) => w.id !== workspaceId);
      await refreshWorkspaces();
      if (remaining.length > 0) switchWorkspace(remaining[0].id);
      else { localStorage.removeItem("vs_workspace"); window.location.reload(); }
      toast.success("Workspace șters");
    } catch (e) { toast.error(e?.response?.data?.detail || "Nu a reușit"); }
  };

  // One row, shared by the member list and the guest list.
  const memberRow = (m) => (
              <li key={m.id} className="px-5 py-3 flex items-center gap-3" data-testid={`member-${m.id}`}>
                <Avatar user={m} size={32} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium">{m.name}</span>
                    {m.id === currentUser.id && <span className="text-[10px] mono text-fg/40">(tu)</span>}
                    {m.is_client && (
                      <span data-testid={`client-badge-${m.id}`}
                        className="text-[10px] mono px-1 border border-line/20 text-fg/50">client</span>
                    )}
                  </div>
                  <div className="text-[11px] text-fg/40 mono truncate">{m.email}</div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {m.role === "owner" && <Crown className="w-3.5 h-3.5 text-brand" />}
                  {m.role === "admin" && <Shield className="w-3.5 h-3.5 text-blue-400" />}
                  {m.role === "guest" && <UserCircle2 className="w-3.5 h-3.5 text-fg-muted" />}
                  <span className="mono text-fg/50 uppercase tracking-wider text-[10px]" data-testid={`role-label-${m.id}`}>{roleLabel(m.role)}</span>
                </div>
                {/* actions menu */}
                {((isAdmin && m.role !== "owner" && m.id !== currentUser.id) || (isOwner && m.id !== currentUser.id)) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`member-actions-${m.id}`} className="p-1.5 text-fg/40 hover:text-fg">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-surface border-line/10 text-fg rounded-none" align="end">
                      {isOwner && m.role !== "admin" && (
                        <DropdownMenuItem data-testid={`promote-admin-${m.id}`} onClick={() => setRole(m.id, "admin")} className="cursor-pointer focus:bg-fg/10">
                          <Shield className="w-3.5 h-3.5 mr-2" /> Fă-l {roleLabel("admin")}
                        </DropdownMenuItem>
                      )}
                      {isOwner && m.role === "admin" && (
                        <DropdownMenuItem data-testid={`demote-member-${m.id}`} onClick={() => setRole(m.id, "member")} className="cursor-pointer focus:bg-fg/10">
                          <Users className="w-3.5 h-3.5 mr-2" /> Retrogradează la {roleLabel("member")}
                        </DropdownMenuItem>
                      )}
                      {m.role === "guest" && (
                        <DropdownMenuItem data-testid={`promote-member-${m.id}`} onClick={() => setRole(m.id, "member")} className="cursor-pointer focus:bg-fg/10">
                          <Users className="w-3.5 h-3.5 mr-2" /> Fă-l {roleLabel("member")}
                        </DropdownMenuItem>
                      )}
                      {/* Cine e client se declară, nu se deduce: în date toți
                          membrii au același rol și aproape toți aceeași
                          terminație de email. Rezumatul de dimineață se sprijină
                          pe marcajul ăsta. */}
                      <DropdownMenuItem data-testid={`toggle-client-${m.id}`}
                        onClick={() => setClient(m.id, !m.is_client)}
                        className="cursor-pointer focus:bg-fg/10">
                        <UserCircle2 className="w-3.5 h-3.5 mr-2" />
                        {m.is_client ? "Nu mai e client" : "Marchează drept client"}
                      </DropdownMenuItem>
                      {m.role === "member" && (
                        <DropdownMenuItem data-testid={`demote-guest-${m.id}`} onClick={() => setRole(m.id, "guest")} className="cursor-pointer focus:bg-fg/10">
                          <UserCircle2 className="w-3.5 h-3.5 mr-2" /> Retrogradează la {roleLabel("guest")}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator className="bg-fg/10" />
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button data-testid={`remove-member-trigger-${m.id}`} className="w-full text-left px-2 py-1.5 text-sm text-red-400 hover:bg-red-500/10 flex items-center">
                            <Trash2 className="w-3.5 h-3.5 mr-2" /> Scoate din workspace
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-surface border-line/10 text-fg rounded-none">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Elimină {m.name}?</AlertDialogTitle>
                            <AlertDialogDescription className="text-fg/60">
                              Va pierde accesul la toate canalele, proiectele și mesajele din acest workspace.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-transparent border-line/10 text-fg hover:bg-fg/5">Anulează</AlertDialogCancel>
                            <AlertDialogAction
                              data-testid={`remove-member-confirm-${m.id}`}
                              onClick={() => removeMember(m.id)}
                              className="bg-red-500 hover:bg-red-600 text-fg"
                            >Elimină</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </li>
  );

  return (
    <div className="h-full flex flex-col">
      <div className="h-14 px-6 flex items-center gap-2 border-b border-line/10 bg-bg">
        <Users className="w-4 h-4 text-fg/50" />
        <h2 className="font-bold tracking-tight">Membri și setări</h2>
        <span className="label-tiny ml-2">// {roleLabel(myRole)}</span>
      </div>

      <div className="flex-1 overflow-y-auto p-6 max-w-3xl space-y-6">
        {/* Workspace info / rename */}
        <section className="bg-surface border border-line/10">
          <div className="px-5 py-4 border-b border-line/10">
            <h3 className="font-bold tracking-tight">Workspace</h3>
          </div>
          <div className="p-5 space-y-3">
            <div className="flex items-center gap-3">
              <span className="label-tiny w-24">Nume</span>
              {renaming && isAdmin ? (
                <div className="flex-1 flex items-center gap-2">
                  <input
                    data-testid="ws-rename-input"
                    autoFocus className="input-flat flex-1"
                    value={newName} onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") rename(); if (e.key === "Escape") setRenaming(false); }}
                  />
                  <button data-testid="ws-rename-save" onClick={rename} className="btn-primary text-sm">Salvează</button>
                  <button onClick={() => setRenaming(false)} className="text-fg/40 hover:text-fg"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <>
                  <span className="text-sm" data-testid="ws-name-display">{current?.name}</span>
                  {isAdmin && (
                    <button data-testid="ws-rename-trigger" onClick={() => setRenaming(true)} className="text-xs text-fg/40 hover:text-brand mono ml-2">editat</button>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="label-tiny w-24">Slug</span>
              <span className="text-sm mono text-fg/70">{current?.slug}</span>
            </div>
          </div>
        </section>

        {/* Invite code */}
        <section className="bg-surface border border-line/10">
          <div className="px-5 py-4 border-b border-line/10 flex items-center justify-between">
            <h3 className="font-bold tracking-tight">Link de invitație</h3>
            <span className="label-tiny">// DE DISTRIBUIT</span>
          </div>
          <div className="p-5 flex items-center gap-3">
            <div className="flex-1 px-4 py-3 bg-surface-2 border border-line/10 mono text-lg tracking-wider" data-testid="invite-code-display">{current?.invite_code}</div>
            <button data-testid="copy-invite-code-button" onClick={copyCode} className="btn-primary flex items-center gap-2">
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copiat" : "Copiază"}
            </button>
            {isAdmin && (
              <button data-testid="regen-code-button" onClick={regenCode} className="btn-ghost flex items-center gap-2" title="Generează un cod nou (îl anulează pe cel vechi)">
                <RefreshCw className="w-4 h-4" />
              </button>
            )}
          </div>
        </section>

        {/* Email invite (admin only) */}
        {isAdmin && (
          <section className="bg-surface border border-line/10">
            <div className="px-5 py-4 border-b border-line/10">
              <h3 className="font-bold tracking-tight">Invită după email</h3>
            </div>
            <form onSubmit={invite} className="p-5 flex items-center gap-3">
              <Mail className="w-4 h-4 text-fg/40" />
              <input
                data-testid="invite-email-input"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="input-flat flex-1" placeholder="coleg@firma.ro"
              />
              <select
                data-testid="invite-role-select"
                aria-label="Rol la invitație"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="input-flat text-sm"
              >
                <option value="member">{roleLabel("member")}</option>
                <option value="guest">{roleLabel("guest")}</option>
              </select>
              <button data-testid="invite-email-button" type="submit" disabled={inviting} className="btn-primary flex items-center gap-2">
                <Send className="w-3.5 h-3.5" /> {inviting ? "..." : "Invită"}
              </button>
            </form>
            {invites.length > 0 && (
              <div className="border-t border-line/10 p-5">
                <p className="label-tiny mb-2">// ÎN AȘTEPTARE ({invites.length})</p>
                <ul className="space-y-1.5">
                  {invites.map((inv) => (
                    <li key={inv.id} className="flex items-center justify-between text-xs">
                      <span className="text-fg/80 mono">{inv.email}</span>
                      <div className="flex items-center gap-3">
                        <span className="label-tiny text-fg-muted uppercase">{inv.role || "member"}</span>
                        <span className="text-fg/40 mono">{dateOnly(inv.created_at)}</span>
                        <button data-testid={`cancel-invite-${inv.id}`} onClick={() => cancelInvite(inv.id)} className="text-fg/40 hover:text-red-400">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {/* Members list */}
        <section className="bg-surface border border-line/10">
          <div className="px-5 py-4 border-b border-line/10 flex items-center justify-between">
            <h3 className="font-bold tracking-tight">Membri</h3>
            <span className="label-tiny">// {staff.length} active</span>
          </div>
          <ul className="divide-y divide-line/5">
            {staff.map(memberRow)}
          </ul>
        </section>

        {guests.length > 0 && (
          <section className="bg-surface border border-line/10" data-testid="guests-section">
            <div className="px-5 py-4 border-b border-line/10 flex items-center justify-between">
              <h3 className="font-bold tracking-tight">Oaspeți</h3>
              <span className="label-tiny">// {guests.length} cu acces limitat</span>
            </div>
            <p className="px-5 pt-4 text-xs text-fg-muted leading-relaxed">
              Un oaspete vede doar canalele și tichetele care îl includ explicit. Nu poate deschide
              tichete, nu poate porni apeluri și nu vede setările workspace-ului.
            </p>
            <ul className="divide-y divide-line/5 mt-2">
              {guests.map(memberRow)}
            </ul>
          </section>
        )}

        {/* Danger zone */}
        <section className="bg-surface border border-red-500/20">
          <div className="px-5 py-4 border-b border-red-500/20 flex items-center gap-2">
            <Trash2 className="w-4 h-4 text-red-400" />
            <h3 className="font-bold tracking-tight">Zonă periculoasă</h3>
          </div>
          <div className="p-5 space-y-3">
            {!isOwner && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid="leave-workspace-trigger" className="btn-ghost flex items-center gap-2 text-red-400 border-red-500/30 hover:bg-red-500/10">
                    <LogOut className="w-4 h-4" /> Ieși din workspace
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-surface border-line/10 text-fg rounded-none">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Ieși din "{current?.name}"?</AlertDialogTitle>
                    <AlertDialogDescription className="text-fg/60">
                      Vei fi scos din acest workspace. Poți reveni mai târziu cu o invitație nouă.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-transparent border-line/10 text-fg hover:bg-fg/5">Anulează</AlertDialogCancel>
                    <AlertDialogAction data-testid="leave-workspace-confirm" onClick={leaveWorkspace} className="bg-red-500 hover:bg-red-600 text-fg">Ieși</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {isOwner && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid="delete-workspace-trigger" className="btn-ghost flex items-center gap-2 text-red-400 border-red-500/30 hover:bg-red-500/10">
                    <Trash2 className="w-4 h-4" /> Șterge workspace-ul
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-surface border-line/10 text-fg rounded-none">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Șterge "{current?.name}"?</AlertDialogTitle>
                    <AlertDialogDescription className="text-fg/60">
                      Șterge definitiv toate canalele, mesajele, proiectele, sarcinile, fragmentele și fișierele, pentru toți membrii. Nu se poate reveni.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-transparent border-line/10 text-fg hover:bg-fg/5">Anulează</AlertDialogCancel>
                    <AlertDialogAction data-testid="delete-workspace-confirm" onClick={deleteWorkspace} className="bg-red-500 hover:bg-red-600 text-fg">Șterge definitiv</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            <p className="text-[11px] text-fg/40 mono">
              {isOwner ? "Doar tu (proprietarul) poți șterge acest workspace." : "Vei pierde accesul la tot ce s-a partajat aici."}
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
